import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { showToast } from '../../../../../../core/utils/alert';
import { all_routes } from '../../../../../router/all_routes';
import { useCreateOfflinePaymentMutation } from '../../../../../feescollection/api/offlinePaymentApi';

const PaymentDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { student, invoices, invoiceNumbers, totalAmount } = location.state;
    const [payableAmount, setPayableAmount] = useState(totalAmount);

    // Offline Payment Mutation
    const [createOfflinePayment, { isLoading: isOfflineCreating }] = useCreateOfflinePaymentMutation();

    // Offline form state
    const [offlineForm, setOfflineForm] = useState({
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        remarks: '',
    });



    const handleOfflineSubmit = async () => {

        const formData = new FormData();
        formData.append('student_id', student.id.toString());
        formData.append('center_id', student.center.id.toString());
        formData.append('amount', payableAmount.toString());
        formData.append('payment_date', offlineForm.payment_date);
        formData.append('payment_method', offlineForm.payment_method);
        if (offlineForm.remarks) formData.append('remarks', offlineForm.remarks);
        // If there are multiple invoices, backend might need them
        if (invoices && invoices.length > 0) {
            invoices.forEach((invId: number) => formData.append('invoice_ids[]', invId.toString()));
        }
        formData.append('status', 'approved');

        try {
            const res = await createOfflinePayment(formData).unwrap();
            if (res.success) {
                showToast({ text: "Offline payment record created successfully!", icon: "success" });
                navigate(all_routes.studentFees
                    .replace(":id", student.id || "")
                    .replace(":studentId", student.id.toString()), {
                        state: {
                            showReceiptForInvoiceId: (invoices && invoices[0]),
                            showReceiptGroupId: (res as any).data?.receipt_group_id || null,
                            offlinePaymentRemarks: offlineForm.remarks || "",
                        }
                    });
            } else {
                showToast({ text: res.message || "Failed to submit offline payment", icon: "error" });
            }
        } catch (err: any) {
            showToast({ text: err?.data?.message || "An error occurred during submission", icon: "error" });
        }
    };

    const isAmountValid = payableAmount > 0 && payableAmount <= totalAmount;
    
    const displayInvoiceStr = invoiceNumbers || `INV-${invoices?.join(', ')}`;

    return (
        <div className="page-wrapper bg-light">
            <div className="content container py-4">
                <div className="row justify-content-center">
                    <div className="col-xxl-8 col-xl-9 col-lg-10">
                        <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
                            <div className="card-header bg-white border-0 py-4 text-center">
                                <h3 className="mb-0 fw-bold" style={{ color: '#003366' }}>
                                    <i className="ti ti-wallet me-2 text-primary"></i>Collect Payment
                                </h3>
                                <p className="text-muted mb-0">Invoice: {displayInvoiceStr}</p>
                            </div>

                            <div className="card-body p-4">
                                {/* Student Summary */}
                                <div className="row mb-4 g-3">
                                    <div className="col-md-6">
                                        <div className="p-3 border rounded-3 h-100" style={{ background: '#f8f9ff' }}>
                                            <small className="text-muted text-uppercase fw-bold ls-1 d-block mb-1">Student</small>
                                            <h5 className="mb-1">{student.name}</h5>
                                            <div className="small text-muted">
                                                <div><i className="ti ti-mail me-1"></i>{student.profile.email}</div>
                                                <div><i className="ti ti-phone me-1"></i>{student.profile.phone}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="p-3 border rounded-3 h-100" style={{ background: '#f8f9ff' }}>
                                            <small className="text-muted text-uppercase fw-bold ls-1 d-block mb-1">Fee Summary</small>
                                            <h4 className="mb-0 text-success fw-bold">₹{totalAmount}</h4>
                                            <div className="small text-muted">Total Outstanding Amount</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Amount Input */}
                                <div className="mb-4">
                                    <label htmlFor="payableAmount" className="form-label fw-bold">Enter Amount to Pay</label>
                                    <div className="input-group input-group-lg">
                                        <span className="input-group-text border-end-0 bg-transparent" style={{ color: '#003366', fontWeight: '600' }}>₹</span>
                                        <input
                                            id="payableAmount"
                                            type="number"
                                            value={payableAmount}
                                            onChange={(e) => setPayableAmount(Number(e.target.value))}
                                            className={`form-control py-4 border-start-0 fw-bold ${!isAmountValid ? 'is-invalid' : ''}`}
                                            placeholder="0.00"
                                            style={{ color: '#003366' }}
                                        />
                                    </div>
                                    {!isAmountValid && (
                                        <div className="invalid-feedback d-block">
                                            Amount must be between 1 and ₹{totalAmount}.
                                        </div>
                                    )}
                                </div>

                                {/* Payment Forms */}
                                    <div className="p-4 border rounded-3 bg-light mb-4">
                                        <h5 className="mb-3">Offline Payment Details</h5>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label small fw-bold">Payment Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    max={new Date().toISOString().split('T')[0]}
                                                    value={offlineForm.payment_date}
                                                    onChange={e => setOfflineForm(prev => ({ ...prev, payment_date: e.target.value }))}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small fw-bold">Method</label>
                                                <select
                                                    className="form-select"
                                                    value={offlineForm.payment_method}
                                                    onChange={e => setOfflineForm(prev => ({ ...prev, payment_method: e.target.value }))}
                                                >
                                                    <option value="cash">Cash</option>
                                                    <option value="mpay">M-Pay</option>
                                                    <option value="bank_transfer">Bank Transfer</option>
                                                    <option value="cheque">Cheque</option>
                                                    <option value="upi">Direct UPI Transfer</option>
                                                </select>
                                            </div>

                                            {/* Remarks */}
                                            <div className="col-12">
                                                <label className="form-label small fw-bold">Remarks <span className="text-muted fw-normal">(optional)</span></label>
                                                <textarea
                                                    className="form-control"
                                                    rows={3}
                                                    placeholder="Any notes or remarks about this payment..."
                                                    value={offlineForm.remarks}
                                                    onChange={e => setOfflineForm(prev => ({ ...prev, remarks: e.target.value }))}
                                                />
                                            </div>

                                        </div>
                                    </div>


                                {/* Submit Buttons */}
                                <div className="d-grid gap-2">
                                        <button
                                            onClick={handleOfflineSubmit}
                                            className="btn btn-success btn-lg p-3 shadow-sm border-0 d-flex align-items-center justify-content-center gap-2"
                                            disabled={isOfflineCreating || !isAmountValid}
                                            style={{ background: 'linear-gradient(135deg, #198754 0%, #0c3e27 100%)' }}
                                        >
                                            {isOfflineCreating ? (
                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                            ) : <i className="ti ti-upload fs-18"></i>}
                                            Submit Offline Record (₹{payableAmount})
                                        </button>

                                    <button
                                        className="btn btn-link text-muted mt-1"
                                        onClick={() => navigate(-1)}
                                    >
                                        Cancel and Go Back
                                    </button>
                                </div>
                            </div>

                            <div className="card-footer bg-light text-center py-3 border-0">
                                <small className="text-muted">
                                    <i className="ti ti-shield-lock me-1"></i>Payments are secure and encrypted
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentDetails;
